/* Cuvée Price Test — client module (v3, variant-swap)
 *
 * Reads window.CUVEE_PT (rendered server-side from product metafields):
 *   {
 *     enabled: true,
 *     product_id: 1613252460605,
 *     product_handle: "shampoo",
 *     control_price_cents: 3500,
 *     cogs_cents: 840,
 *     variants: [
 *       { id: "control", price_cents: 3500, weight: 1, shopify_variant_id: 12345 },
 *       { id: "a",       price_cents: 3000, weight: 1, shopify_variant_id: 67890 },
 *       { id: "b",       price_cents: 2700, weight: 1, shopify_variant_id: 24680 }
 *     ]
 *   }
 *
 * How v3 works (no Shopify discount codes — clean willingness-to-pay test):
 *
 *   1. The merchant adds a hidden "Pricing" option to the product with one variant
 *      per test arm. Each variant is priced at its arm's test price. The "Pricing"
 *      option is hidden from the customer-facing variant picker via CSS.
 *
 *   2. On PDP load, this module:
 *        - bot-detects + dwells
 *        - reads or writes the cookie that pins the visitor to an arm
 *        - swaps the buy-form's `id` input to the assigned arm's shopify_variant_id
 *        - paints the displayed price to the arm's price (defensive — Shopify will
 *          also paint it correctly because the form variant changed)
 *        - re-applies on variant change (size selector etc.)
 *        - marks the form with line-item properties for analytics labeling
 *        - fires GA4 + Klaviyo telemetry
 *
 *   3. ATC submits the form with the test variant's id. Shopify charges the
 *      variant's actual price. No discount logic, no deal-perception bias.
 *
 *   4. On the order, the line item references the test variant. Order webhook
 *      can identify the arm by variant_id alone, with line-item properties as
 *      additional analytics labels.
 */
(function () {
  'use strict';

  var CFG = window.CUVEE_PT;
  if (!CFG || !CFG.enabled) return;
  if (!Array.isArray(CFG.variants) || CFG.variants.length < 2) {
    if (window.console) console.warn('[cuvee-pt] config has fewer than 2 variants — exiting.');
    return;
  }
  // Every variant needs a real Shopify variant id for v3
  for (var vi = 0; vi < CFG.variants.length; vi++) {
    if (!CFG.variants[vi].shopify_variant_id) {
      console.warn('[cuvee-pt] variant', CFG.variants[vi].id, 'is missing shopify_variant_id — exiting.');
      return;
    }
  }

  // ---- bot detection
  if (navigator.webdriver) return;
  var ua = (navigator.userAgent || '').toLowerCase();
  if (/(googlebot|bingbot|yandex|duckduck|baidu|slurp|crawler|spider|bot\b|headlesschrome|phantomjs|puppeteer|playwright)/.test(ua)) return;

  var COOKIE_KEY = 'cuvee_pt_' + CFG.product_id;
  var COOKIE_TTL_DAYS = 180;
  var ASSIGN_DWELL_MS = 1500;

  // ---- cookie helpers
  function readCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m[2]) : null;
  }
  function writeCookie(name, value, days) {
    var d = new Date(); d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  // ---- variant selection (weighted random)
  function pickVariant() {
    var sum = CFG.variants.reduce(function (a, v) { return a + (v.weight > 0 ? v.weight : 0); }, 0);
    if (sum <= 0) return CFG.variants[0];
    var r = Math.random() * sum;
    for (var i = 0; i < CFG.variants.length; i++) {
      var w = CFG.variants[i].weight > 0 ? CFG.variants[i].weight : 0;
      if (r < w) return CFG.variants[i];
      r -= w;
    }
    return CFG.variants[CFG.variants.length - 1];
  }
  function findVariantById(id) {
    for (var i = 0; i < CFG.variants.length; i++) {
      if (CFG.variants[i].id === id) return CFG.variants[i];
    }
    return null;
  }

  // ---- price formatting
  function formatMoney(cents) {
    try {
      if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  // ---- analytics
  function track(event, props) {
    var payload = Object.assign({
      product_id: CFG.product_id,
      product_handle: CFG.product_handle,
      cogs_cents: CFG.cogs_cents
    }, props);
    if (window.dataLayer) window.dataLayer.push(Object.assign({ event: event }, payload));
    try {
      if (window._learnq && event === 'price_test_assigned') {
        var key = 'cuvee_pt_' + CFG.product_handle;
        var ident = {};
        ident[key] = props.variant_id;
        window._learnq.push(['identify', ident]);
      }
    } catch (e) {}
  }

  // ---- locate ATC form(s) for the test product (NOT cart drawer / recommendations)
  function findProductForms() {
    var forms = [];
    var all = document.querySelectorAll('form[action*="/cart/add"]');
    all.forEach(function (f) {
      var pidAttr = f.getAttribute('data-product-id') ||
        (f.closest('[data-product-id]') && f.closest('[data-product-id]').getAttribute('data-product-id'));
      if (pidAttr && String(pidAttr) === String(CFG.product_id)) { forms.push(f); return; }
      var handleHint = f.closest('[data-product-handle], [data-product-url]');
      if (handleHint) {
        var url = handleHint.getAttribute('data-product-url') || handleHint.getAttribute('data-product-handle') || '';
        if (url.indexOf(CFG.product_handle) >= 0) { forms.push(f); return; }
      }
      // Last resort: form is on the main PDP layout (not in a drawer or modal)
      var inDrawer = f.closest('cart-drawer, [drawer], [data-cart-drawer], .cart--draw, dialog');
      if (!inDrawer) forms.push(f);
    });
    return forms;
  }

  // ---- build the set of all variant ids that participate in the test
  //      (control + every arm). The test only applies when the customer's
  //      currently-selected variant is one of these — otherwise (e.g. they
  //      switched to a different size that's not part of the test), we hold off.
  var TEST_VARIANT_IDS = (function () {
    var s = {};
    CFG.variants.forEach(function (v) { s[String(v.shopify_variant_id)] = true; });
    return s;
  })();

  // Is the form's currently-selected variant eligible for the test?
  function isFormOnTestVariant(form) {
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return false;
    return TEST_VARIANT_IDS[String(idInput.value)] === true;
  }

  // ---- swap the form's variant id to the test arm's shopify_variant_id
  //      ONLY if the form is currently on a test-eligible variant. If the
  //      buyer switched to a different size (not part of the test), we leave
  //      the form alone so they ATC the variant they actually selected.
  function swapFormVariant(form, variant) {
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    if (!isFormOnTestVariant(form)) return; // not on a test-eligible variant — bail
    if (idInput.value !== String(variant.shopify_variant_id)) {
      idInput.setAttribute('data-cuvee-pt-original', idInput.value);
      idInput.value = String(variant.shopify_variant_id);
    }
  }

  // ---- update displayed price (defensive — Shopify will also paint via the variant change,
  //      but on first load we paint immediately to avoid a flash of the control price)
  function paintPrice(variant) {
    var formatted = formatMoney(variant.price_cents);
    var scope = document.querySelector('main, [role="main"]') || document;
    scope.querySelectorAll([
      '.product__info-wrapper .price__number .money',
      '.product__info-wrapper .price .money',
      '[data-product-price] .money',
      '.product-single__box .price__number .money'
    ].join(',')).forEach(function (n) {
      n.textContent = formatted;
      n.setAttribute('data-cuvee-pt-painted', '1');
    });
    // Suppress compare-at on non-control arms (would otherwise read as a discount)
    if (variant.id !== 'control') {
      scope.querySelectorAll('.price__compare, .cuvee-price-value').forEach(function (n) {
        n.style.display = 'none';
      });
    }
  }

  // ---- mark the form with line-item properties (for analytics labels — the variant_id is
  //      already the source of truth, but these make GA4/order filtering simpler)
  function markCartForm(form, variant) {
    ['variant', 'price'].forEach(function (k) {
      var sel = 'input[name="properties[_pt_' + k + ']"]';
      var existing = form.querySelector(sel);
      if (existing) existing.parentNode.removeChild(existing);
    });
    var i1 = document.createElement('input');
    i1.type = 'hidden';
    i1.name = 'properties[_pt_variant]';
    i1.value = variant.id;
    form.appendChild(i1);
    var i2 = document.createElement('input');
    i2.type = 'hidden';
    i2.name = 'properties[_pt_price]';
    i2.value = String(variant.price_cents);
    form.appendChild(i2);
  }

  // ---- ATC analytics (no discount logic in v3)
  function wireAtcHandler(variant) {
    document.addEventListener('submit', function (ev) {
      var f = ev.target;
      if (!f || !f.action || f.action.indexOf('/cart/add') < 0) return;
      // Only react to forms we marked
      if (!f.querySelector('input[name="properties[_pt_variant]"]')) return;
      track('price_test_atc', {
        variant_id: variant.id,
        variant_price_cents: variant.price_cents
      });
    }, true);
  }

  // ---- when the buyer changes a customer-visible option (size etc.), the theme
  //      re-renders the form with a different variant id. If the new variant is
  //      part of the test, we re-swap + re-paint. If it's NOT (e.g. user switched
  //      to a different size), we step aside and let the theme handle it normally.
  function wireVariantChangeRepaint(variant) {
    var rerun = function () {
      var anyOnTest = false;
      findProductForms().forEach(function (f) {
        if (isFormOnTestVariant(f)) {
          swapFormVariant(f, variant);
          markCartForm(f, variant);
          anyOnTest = true;
        }
      });
      if (anyOnTest) paintPrice(variant);
      // If no form is on a test-eligible variant, the theme is showing a different
      // size's regular price — leave it alone. We don't unset our paint flag because
      // the next return to the test-eligible size will re-trigger via this same path.
    };
    document.addEventListener('variant:change', rerun);
    document.querySelectorAll('variant-selects, variant-radios').forEach(function (el) {
      el.addEventListener('change', function () { setTimeout(rerun, 50); });
    });
    var priceNode = document.querySelector('.product__info-wrapper .price__number .money, [data-product-price] .money');
    if (priceNode && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        // Only react if our paint flag was set (indicating we previously painted)
        if (priceNode.getAttribute('data-cuvee-pt-painted') !== '1') return;
        // If the form moved off the test variant, clear our paint flag and let the theme own this node.
        var onTest = false;
        findProductForms().forEach(function (f) { if (isFormOnTestVariant(f)) onTest = true; });
        if (!onTest) {
          priceNode.removeAttribute('data-cuvee-pt-painted');
          return;
        }
        if (priceNode.textContent !== formatMoney(variant.price_cents)) rerun();
      });
      observer.observe(priceNode, { childList: true, characterData: true, subtree: true });
    }
  }

  // ---- belt-and-suspenders: hide the "Pricing" option in the variant picker.
  //      CSS :has() handles modern browsers; this JS handles older + edge cases
  //      where markup differs. Walks every Pricing radio/select and hides its
  //      enclosing fieldset / selector-wrapper.
  function hidePricingOptionUI() {
    var sels = [
      'input[name="Pricing" i]',
      'input[name="pricing" i]',
      'select[name="options[Pricing]"]',
      'select[name="options[pricing]"]',
    ];
    document.querySelectorAll(sels.join(',')).forEach(function (el) {
      var wrapper = el.closest('fieldset, .product-form__swatch, .selector-wrapper, .product-form__input');
      if (wrapper) {
        wrapper.style.display = 'none';
        wrapper.setAttribute('data-cuvee-pt-hidden-option', '1');
      }
    });
  }

  // Reveal the price by clearing the pre-paint hide class set by the snippet's inline script.
  function reveal() {
    document.documentElement.classList.remove('cuvee-pt-pending');
  }

  // ---- bootstrap (visual paint runs IMMEDIATELY so there's no flash;
  //      analytics event still defers by ASSIGN_DWELL_MS to filter bot drive-bys)
  function start() {
    hidePricingOptionUI(); // hide picker option immediately so it never flashes visible

    var existing = readCookie(COOKIE_KEY);
    var variant = existing ? findVariantById(existing) : null;
    var isNew = false;
    if (!variant) {
      variant = pickVariant();
      isNew = true;
      writeCookie(COOKIE_KEY, variant.id, COOKIE_TTL_DAYS);
    }
    var forms = findProductForms();
    var anyOnTest = false;
    forms.forEach(function (f) {
      if (isFormOnTestVariant(f)) {
        swapFormVariant(f, variant);
        markCartForm(f, variant);
        anyOnTest = true;
      }
    });
    if (anyOnTest) paintPrice(variant);
    wireAtcHandler(variant);
    wireVariantChangeRepaint(variant);
    reveal(); // unhide the price node now that we've painted

    // Defer the analytics event by the dwell — this is the bot filter (a bot that bounces
    // before 1.5s never appears in our visitor count, so the assignment denominator stays clean).
    // The visual paint above already happened, so visitors don't see a flash.
    setTimeout(function () {
      track(isNew ? 'price_test_assigned' : 'price_test_returning', {
        variant_id: variant.id,
        variant_price_cents: variant.price_cents
      });
    }, ASSIGN_DWELL_MS);

    window.CUVEE_PT_ASSIGNED = variant;
  }

  // Run as early as possible. The script tag is now non-defer so it executes during HTML
  // parse — but DOM elements (forms, price nodes) may not exist yet. We use DOMContentLoaded
  // (or document-ready check) to ensure the price node is in the DOM, then paint without delay.
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
